import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState, Feld,
  FormSection, KpiCard, KpiReihe, LoadingState, StatusBadge,
  type Spalte, type Zustand,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Sammelfunktionen — wo Automatisierung am meisten spart.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * IMMER MIT LISTE ZUR DURCHSICHT UND EINZELNER ABWAHL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein Sammelknopf ohne Vorschau ist ein Sammelrisiko. Deshalb steht auf dieser
 * Seite die Liste ZUERST und der Knopf darunter — und der Knopf ist gesperrt,
 * solange keine Liste geholt wurde.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * UND ÜBERNEHMEN GIBT ES HIER NICHT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Übernehmen heisst, dass sich ein Bauteil bewegt, und das geschieht in Revit.
 * Ein Knopf im Frontend, der „übernommen" setzt, wäre die gefährlichste
 * Schaltfläche im ganzen Modul: Er behauptet, die Leitung liege woanders, und
 * niemand hat sie angefasst. Beim nächsten Leselauf stünde die Kollision
 * wieder da — und gälte als Regression.
 */
export function Sammelaktionen() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [bereich, setBereich] = useState('');
  const [gewerk, setGewerk] = useState('');
  const [gegenGewerk, setGegenGewerk] = useState('');
  const [richtung, setRichtung] = useState('');
  const [betragBis, setBetragBis] = useState<number | null>(50);
  const [nurUnstrittig, setNurUnstrittig] = useState(true);

  const [v, setV] = useState<any | null>(null);
  const [abgewaehlt, setAbgewaehlt] = useState<Set<string>>(new Set());
  const [gruende, setGruende] = useState<any[]>([]);
  const [grund, setGrund] = useState('');
  const [angabe, setAngabe] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [ergebnis, setErgebnis] = useState<any | null>(null);
  const [frage, setFrage] = useState(false);

  useEffect(() => {
    if (!projekt) return;

    api.koordinationAblehngruende(projekt.id)
      .then((x: any) => setGruende(x?.gruende ?? []))
      .catch(() => setGruende([]));
  }, [api, projekt]);

  const wunsch = () => ({
    bereich: bereich || undefined,
    gewerk: gewerk || undefined,
    gegenGewerk: gegenGewerk || undefined,
    richtung: richtung || undefined,
    betragBisMm: betragBis ?? undefined,
    nurUnstrittig,
  });

  const holen = async () => {
    if (!projekt) return;

    setLaeuft(true);
    setFehler(null);
    setErgebnis(null);
    setAbgewaehlt(new Set());

    try {
      setV(await api.sammelVorschau(projekt.id, wunsch()));
    } catch (e) {
      setFehler(e);
      setV(null);
    } finally {
      setLaeuft(false);
    }
  };

  const ablehnen = async () => {
    if (!projekt || !v) return;

    setLaeuft(true);
    setFehler(null);

    try {
      setErgebnis(await api.sammelAblehnen(projekt.id, {
        ...wunsch(),
        abgewaehlt: [...abgewaehlt],
        grundschluessel: grund,
        angabe: angabe || undefined,
      }));

      setV(null);
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  const posten = useMemo(() => (v?.posten ?? []) as any[], [v]);

  const spalten: Spalte<any>[] = useMemo(() => [
    {
      schluessel: 'bereich', titel: 'Abschnitt', immerSichtbar: true,
      wert: (p) => p.bereich ?? '',
      inhalt: (p) => p.bereich ?? '—',
    },
    {
      schluessel: 'geschoss', titel: 'Geschoss',
      wert: (p) => p.geschoss ?? '',
      inhalt: (p) => p.geschoss ?? '—',
    },
    {
      schluessel: 'gewerke', titel: 'Gewerke',
      wert: (p) => `${p.gewerkA} × ${p.gewerkB}`,
    },
    {
      schluessel: 'richtung', titel: 'Richtung',
      wert: (p) => p.richtung ?? '',
      inhalt: (p) => p.richtung ?? '—',
    },
    {
      schluessel: 'betrag', titel: 'Betrag', art: 'zahl', einheit: 'mm',
      nachkomma: 0,
      wert: (p) => p.betragMm,
    },
    {
      schluessel: 'durchdringung', titel: 'Durchdringung', art: 'zahl',
      einheit: 'mm', nachkomma: 0,
      wert: (p) => p.durchdringungMm,
    },
    {
      schluessel: 'vorbehalt', titel: 'Vorbehalt', art: 'status', breite: '200px',
      wert: (p) => p.vorbehalt ?? 'unstrittig',
      inhalt: (p) => (
        <StatusBadge
          zustand={(p.unstrittig ? 'gut' : 'warnung') as Zustand}
          text={p.vorbehalt ?? 'unstrittig'}
          klein
        />
      ),
    },
  ], []);

  const kopf = (
    <PageHeader
      titel="Sammelfunktionen"
      beschreibung={'„Alle Höhenversätze unter 50 mm in einem Abschnitt" — gleiche '
        + 'Ursache, gleiche Lösung, oft dutzendfach. Immer mit Liste zur Durchsicht '
        + 'und einzelner Abwahl: Ein Sammelknopf ohne Vorschau ist ein Sammelrisiko.'}
      aktionen={projekt && (
        <Link to={`/projekte/${projekt.id}/koordination`} className="m-knopf" style={{ textDecoration: 'none' }}>
          Koordination
        </Link>
      )}
    />
  );

  if (!projekt) {
    return (
      <>
        {kopf}
        <LoadingState text="Projekt wird geladen …" zeilen={5} />
      </>
    );
  }

  const gewaehlterGrund = gruende.find((g: any) => g.schluessel === grund);

  const betroffen = posten.length - abgewaehlt.size;

  const bereitZumAblehnen =
    v !== null
    && grund !== ''
    && (!gewaehlterGrund?.brauchtAngabe || angabe.trim() !== '')
    && betroffen > 0;

  return (
    <>
      {kopf}

      <FormSection
        titel="Auswahl"
        spalten={3}
        fuss={(
          <button className="m-knopf m-knopf-haupt" onClick={holen} disabled={laeuft}>
            {laeuft ? 'wird geholt …' : 'Liste holen'}
          </button>
        )}
      >
        <Feld
          beschriftung="Abschnitt"
          wert={bereich}
          beiAenderung={(w) => setBereich(w as string)}
          platzhalter="Abschnitt 3"
        />
        <Feld
          art="auswahl"
          beschriftung="Gewerk"
          wert={gewerk}
          beiAenderung={(w) => setGewerk(w as string)}
          optionen={[
            { wert: '', beschriftung: 'alle' },
            ...['rlt', 'hzg', 'klt', 'san', 'spr', 'elt', 'rohr']
              .map((g) => ({ wert: g, beschriftung: g })),
          ]}
        />
        <Feld
          art="auswahl"
          beschriftung="gegen"
          wert={gegenGewerk}
          beiAenderung={(w) => setGegenGewerk(w as string)}
          optionen={[
            { wert: '', beschriftung: 'alle' },
            ...['architektur', 'tragwerk', 'rlt', 'hzg', 'klt', 'san', 'spr', 'elt']
              .map((g) => ({ wert: g, beschriftung: g })),
          ]}
        />
        <Feld
          art="auswahl"
          beschriftung="Richtung"
          wert={richtung}
          beiAenderung={(w) => setRichtung(w as string)}
          optionen={[
            { wert: '', beschriftung: 'alle' },
            { wert: 'hoch', beschriftung: 'hoch' },
            { wert: 'runter', beschriftung: 'runter' },
            { wert: 'seitlich_x', beschriftung: 'seitlich X' },
            { wert: 'seitlich_y', beschriftung: 'seitlich Y' },
          ]}
        />
        <Feld
          art="zahl"
          beschriftung="Betrag bis"
          einheit="mm"
          wert={betragBis}
          beiAenderung={(w) => setBetragBis(w as number | null)}
          min={0}
        />
        <Feld
          art="haken"
          beschriftung="nur unstrittige"
          wert={nurUnstrittig}
          beiAenderung={(w) => setNurUnstrittig(w as boolean)}
        />
      </FormSection>

      {fehler !== null && (
        <ErrorState
          meldung="Die Sammelaktion ist fehlgeschlagen."
          technisch={fehler}
        />
      )}

      {ergebnis && (
        <ContentSection titel="Ausgeführt">
          <KpiReihe>
            <KpiCard titel="Abgelehnt" wert={ergebnis.betroffen} />
            <KpiCard titel="Abgewählt" wert={ergebnis.abgewaehlt} />
          </KpiReihe>

          {/* Die Gegenprobe wörtlich — mit den Zeilen, die NICHT prüffähig
              sind. Eine Gegenprobe, die drei OK meldet, von denen zwei nicht
              geprüft wurden, ist schlimmer als keine. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {((ergebnis.gegenprobe ?? []) as string[]).map((g, i) => (
              <StatusBadge
                key={i}
                zustand={g.startsWith('FEHLER') ? 'fehler'
                  : g.startsWith('NICHT') ? 'warnung' : 'gut'}
                text={g}
                klein
              />
            ))}
          </div>

          {((ergebnis.hinweise ?? []) as string[]).map((h, i) => (
            <p key={i} className="schwach" style={{ fontSize: 12 }}>{h}</p>
          ))}
        </ContentSection>
      )}

      {v && (
        <>
          <KpiReihe>
            <KpiCard titel="Treffen die Auswahl" wert={v.gesamt} />
            <KpiCard
              titel="Unstrittig" wert={v.unstrittig}
              zustand={v.unstrittig > 0 ? 'gut' : undefined}
              zustandText={v.unstrittig > 0 ? 'ohne Vorbehalt' : undefined}
            />
            <KpiCard
              titel="Mit Vorbehalt" wert={v.mitVorbehalt}
              zustand={v.mitVorbehalt > 0 ? 'warnung' : undefined}
              zustandText={v.mitVorbehalt > 0 ? 'einzeln ansehen' : undefined}
            />
            <KpiCard titel="Abgewählt" wert={abgewaehlt.size} />
          </KpiReihe>

          {((v.hinweise ?? []) as string[]).map((h: string, i: number) => (
            <p key={i} className="schwach" style={{ fontSize: 12 }}>{h}</p>
          ))}

          <FormSection
            titel="Ablehnen"
            spalten={2}
            fuss={(
              <button
                className="m-knopf m-knopf-gefahr"
                onClick={() => setFrage(true)}
                disabled={laeuft || !bereitZumAblehnen}
              >
                {betroffen} Aufgaben ablehnen
              </button>
            )}
          >
            <Feld
              art="auswahl"
              beschriftung="Ablehngrund"
              hinweis="Aus der Liste, nicht als Freitext."
              pflicht
              wert={grund}
              beiAenderung={(w) => { setGrund(w as string); setAngabe(''); }}
              optionen={[
                { wert: '', beschriftung: '— wählen —' },
                ...gruende.map((g: any) => ({
                  wert: g.schluessel as string,
                  beschriftung: `${g.text}${g.istEskalation ? ' (Eskalation)' : ''}`,
                })),
              ]}
            />

            {gewaehlterGrund?.brauchtAngabe && (
              <Feld
                beschriftung={gewaehlterGrund.angabeText}
                wert={angabe}
                beiAenderung={(w) => setAngabe(w as string)}
                pflicht
              />
            )}
          </FormSection>

          {gewaehlterGrund?.istEskalation && (
            <div className="hinweis warnung">
              <span className="hinweis-zeichen">!</span>
              <div>
                Dieser Grund ist eine <strong>Eskalation</strong> und keine
                Erledigung: Die Aufgaben gehen weiter und werden nicht
                geschlossen. In der Ansicht der Projektleitung erscheinen sie
                als Blockade.
              </div>
            </div>
          )}

          <p className="schwach" style={{ fontSize: 12 }}>
            <strong>Übernehmen gibt es hier nicht.</strong> Übernehmen heisst,
            dass sich ein Bauteil bewegt, und das geschieht in Revit. Ein Knopf,
            der hier „übernommen" setzt, würde behaupten, die Leitung liege
            woanders — und beim nächsten Leselauf stünde die Kollision wieder
            da, als Regression.
          </p>

          <DataTable
            name={v.beschreibung}
            beschreibung="Der Haken bedeutet: Diese Aufgabe wird abgelehnt."
            spalten={spalten}
            zeilen={posten}
            schluessel={(p) => p.kollisionId}
            ausfuhr
            ausfuhrDateiname="Sammelaktion"
            // Die Tabelle wählt, was BETROFFEN ist; `abgewaehlt` ist die
            // Gegenmenge - so herum verlangt es der Endpunkt.
            auswahl={{
              gewaehlt: posten
                .map((p) => p.kollisionId as string)
                .filter((id) => !abgewaehlt.has(id)),
              beiAuswahl: (ids) => {
                const gewaehlt = new Set(ids);
                setAbgewaehlt(new Set(
                  posten.map((p) => p.kollisionId as string)
                    .filter((id) => !gewaehlt.has(id)),
                ));
              },
            }}
            leer={(
              <EmptyState
                titel="Keine Aufgabe trifft diese Auswahl."
                erklaerung={'Der Filter oben ist zu eng — oder in diesem Abschnitt ist '
                  + 'alles entschieden.'}
              />
            )}
          />
        </>
      )}

      <ConfirmDialog
        offen={frage}
        art="warnung"
        titel={`${betroffen} Aufgaben ablehnen?`}
        text={'Das ist eine Entscheidung, die im Protokoll steht und den nächsten '
          + 'Lauf überlebt.'}
        bestaetigenText="Ablehnen"
        laeuft={laeuft}
        beiBestaetigen={async () => { setFrage(false); await ablehnen(); }}
        beiAbbrechen={() => setFrage(false)}
      />
    </>
  );
}
